/**
 * Xero — dedicated accounting workspace (BLU-456). A surface, not a settings
 * panel: persistent connection header, page-level KPI strip, and five
 * URL-synced tabs (Overview / Sync / COGS / Stock-in-Transit / Chart of
 * Accounts). When Xero isn't connected (or the token expired) the tabs are
 * replaced by a centred fallback card.
 *
 * Reads need a Xero-connected org; settings mutations are admin-only and gated
 * inside each card (mirrors the backend: coordinatorProcedure reads,
 * adminProcedure writes). The connect flow reuses the same getAuthUrl redirect
 * Settings uses, so the OAuth callback still lands on /settings unchanged.
 */
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import RoleGuard from '@/components/auth/RoleGuard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BackToTopPill } from '@/components/BackToTopPill';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { useRole } from '@/hooks/use-role';
import {
  XeroHeader,
  XeroPageKpis,
  DisconnectedState,
  OverviewTab,
  SyncTab,
  CogsJournalsCard,
  SitReclassCard,
  ChartOfAccountsTab,
  isWorkspaceReady,
  disconnectedVariant,
  buildActivityFeed,
  sumPostedAmount,
} from '@/features/xero-workspace';

const TABS = ['overview', 'sync', 'cogs', 'sit', 'accounts'] as const;
type XeroTab = (typeof TABS)[number];

const isXeroTab = (value: string | null): value is XeroTab =>
  value !== null && (TABS as readonly string[]).includes(value);

export default function Xero() {
  const utils = trpc.useUtils();
  const { isAdmin } = useRole();
  const admin = isAdmin();
  const [searchParams, setSearchParams] = useSearchParams();

  usePageTitle('Xero', 'Books-truth integration. Manage sync, COGS, and stock-in-transit reclass.');

  const tab: XeroTab = isXeroTab(searchParams.get('tab')) ? (searchParams.get('tab') as XeroTab) : 'overview';

  const handleTabChange = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'overview') {
      params.delete('tab');
    } else {
      params.set('tab', next);
    }
    setSearchParams(params, { replace: true });
  };

  const statusQuery = trpc.xero.getStatus.useQuery(undefined, { staleTime: 30_000 });
  const status = statusQuery.data;
  const ready = isWorkspaceReady(status);

  // COGS/SIT stats power the KPI strip; only fetched once the workspace is live.
  const cogsStatsQuery = trpc.xero.getCogsStats.useQuery(undefined, {
    enabled: ready,
    staleTime: 30_000,
  });
  const sitStatsQuery = trpc.xero.getSitStats.useQuery(undefined, {
    enabled: ready,
    staleTime: 30_000,
  });

  // The posted-dollar KPI + reconciliation hint are summed from the same logs
  // the Overview feed renders, so there's no extra round-trip for the figure.
  const cogsLogQuery = trpc.xero.getCogsLog.useQuery(
    { limit: 20 },
    { enabled: ready, staleTime: 30_000 }
  );
  const sitLogQuery = trpc.xero.getSitLog.useQuery(
    { limit: 20 },
    { enabled: ready, staleTime: 30_000 }
  );

  const postedAmount = useMemo(() => {
    if (!cogsLogQuery.data && !sitLogQuery.data) return null;
    return sumPostedAmount(buildActivityFeed(cogsLogQuery.data, sitLogQuery.data, undefined));
  }, [cogsLogQuery.data, sitLogQuery.data]);

  const [connecting, setConnecting] = useState(false);
  const connectMutation = trpc.xero.getAuthUrl.useMutation({
    onSuccess: (result) => {
      if (result.authUrl) {
        window.location.href = result.authUrl;
        return;
      }
      setConnecting(false);
    },
    onError: (error) => {
      setConnecting(false);
      toast.error(error.message || 'Failed to start Xero connection');
    },
  });

  const disconnectMutation = trpc.xero.disconnect.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.xero.getStatus.invalidate(),
        utils.xero.getAccounts.invalidate(),
        utils.xero.getSyncLogs.invalidate(),
      ]);
      toast.success('Disconnected from Xero');
    },
    onError: (error) => toast.error(error.message || 'Failed to disconnect from Xero'),
  });

  const handleConnect = () => {
    if (connectMutation.isPending) return;
    setConnecting(true);
    connectMutation.mutate({});
  };

  return (
    <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator']}>
      <div className="flex flex-col">
        <main id="main" className="w-full flex-1 space-y-6 px-4 py-6 sm:px-6 lg:px-8">
          {statusQuery.isLoading ? (
            <div className="space-y-6">
              <div className="h-14 animate-pulse rounded-lg border bg-muted/40" />
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="h-20 animate-pulse rounded-lg border bg-muted/40" />
                ))}
              </div>
            </div>
          ) : !status || !ready ? (
            <DisconnectedState
              variant={disconnectedVariant(status)}
              isAdmin={admin}
              onConnect={handleConnect}
              connecting={connecting || connectMutation.isPending}
            />
          ) : (
            <>
              <XeroHeader
                status={status}
                isAdmin={admin}
                onConnect={handleConnect}
                onDisconnect={() => disconnectMutation.mutate()}
                connecting={connecting || connectMutation.isPending}
                disconnecting={disconnectMutation.isPending}
              />

              <XeroPageKpis
                cogsStats={cogsStatsQuery.data}
                sitStats={sitStatsQuery.data}
                postedAmount={postedAmount}
              />

              <Tabs value={tab} onValueChange={handleTabChange} className="space-y-4">
                <div className="overflow-x-auto">
                  <TabsList>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="sync">Sync</TabsTrigger>
                    <TabsTrigger value="cogs">COGS</TabsTrigger>
                    <TabsTrigger value="sit">Stock-in-Transit</TabsTrigger>
                    <TabsTrigger value="accounts">Chart of Accounts</TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="overview">
                  <OverviewTab
                    status={status}
                    cogsStats={cogsStatsQuery.data}
                    sitStats={sitStatsQuery.data}
                    postedAmount={postedAmount}
                    isAdmin={admin}
                  />
                </TabsContent>
                <TabsContent value="sync">
                  <SyncTab />
                </TabsContent>
                <TabsContent value="cogs">
                  <CogsJournalsCard isAdmin={admin} />
                </TabsContent>
                <TabsContent value="sit">
                  <SitReclassCard isAdmin={admin} />
                </TabsContent>
                <TabsContent value="accounts">
                  <ChartOfAccountsTab isAdmin={admin} />
                </TabsContent>
              </Tabs>
            </>
          )}
        </main>
      </div>
      <BackToTopPill />
    </RoleGuard>
  );
}
